import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MSG,
  postLogin,
  postCompleteLink,
  postBootstrapPassword,
  getProviders,
} from '../api/loginApi';
import { readSession, writeSession } from '../../../../services/linkedServices';
import { useSwal } from '../../../../components/Notifications/swal/SwalCom';

/* SweetAlert takes HTML, and everything interesting in these messages was
   written by somebody else — a site name and an email address that came off a
   ProWatchly account document, a refusal sentence from the API. Rendered into
   `html:` unescaped, a site called `<img onerror=...>` would run. The form
   renders the same strings as TEXT in JSX, which is safe by construction; this
   is the one path that is not. */
const escapeHtml = (value) =>
  String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const providerLabel = (list, key) => {
  const found = (list || []).find((p) => p && p.key === key);
  return (found && found.label) || key || 'LabelFlow';
};

/**
 * What the sign-in actually achieved, in one dialog.
 *
 * A cascade sign-in can quietly do three things at once — prove who you are,
 * discover a service account on the same credentials, and attach it — and until
 * this was on screen the only evidence any of it had happened was a deployment
 * target appearing on a model page days later. So it is stated: which provider
 * answered for you, and which sites you can deploy to.
 *
 * "None yet" is a real answer and is printed rather than left blank: a
 * LabelFlow-only workspace has no targets, and that is the normal state, not a
 * failure.
 */
const summarise = (data) => {
  const targets = Array.isArray(data?.targets) ? data.targets : [];
  const primary = providerLabel(data?.providers, data?.primaryProvider);

  const lines = [`<p>Signed in with <b>${escapeHtml(primary)}</b>.</p>`];

  if (targets.length) {
    const items = targets
      .map((t) => {
        const label = escapeHtml(t?.descriptor?.label || t?.label || t?.provider || 'Service');
        const site = escapeHtml(t?.name || t?.email || t?.externalId || '');
        const stale = t?.usable === false || t?.stale === true ? ' (needs re-checking)' : '';
        return `<li>${label}${site ? ` — ${site}` : ''}${stale}</li>`;
      })
      .join('');
    lines.push(`<p>Deployment targets connected:</p><ul>${items}</ul>`);
  } else {
    lines.push('<p>No deployment targets are connected to this workspace yet.</p>');
  }

  return lines.join('');
};

/**
 * The login screen's brain.
 *
 * ONE EMAIL, ONE PASSWORD, NO PROVIDER PICKER. The form has exactly the two
 * fields it always had. The server walks the providers itself, because the
 * person signing in should not have to know which company holds the account
 * they are typing — and a picker would make them declare it before anyone had
 * told them they were wrong.
 *
 * What changed is everything AFTER the request. `/auth_management/login` no
 * longer answers "a user document or nothing"; it answers a message code, and
 * five of those codes are different situations with different remedies:
 *
 *   MSG_100 + mustSetPassword  the workspace was created from a service sign-in
 *                              and has no LabelFlow password yet -> ask for one,
 *                              and DO NOT persist a session until it is set.
 *   MSG_LINK_REQUIRED          the service credentials proved a site, and a
 *                              LabelFlow account already sits on that address.
 *                              Ask for ITS password. Anything else would attach
 *                              a stranger's site to somebody's workspace:
 *                              LabelFlow is self-signup with no email
 *                              verification, so a matching address proves
 *                              nothing at all.
 *   MSG_REFUSED                the credentials are real and are not usable here
 *                              (a Gemmiz platform administrator owns no site; a
 *                              sub-user is not the account owner). The server
 *                              wrote that sentence; it is printed verbatim, so
 *                              a new refusal reason needs no frontend deploy.
 *   MSG_429                    throttled. "Wrong password" here is a lie that
 *                              costs them another attempt.
 *   MSG_401                    genuinely wrong.
 *
 * Collapsing any of those into "Wrong email or password" is the bug this hook
 * exists to prevent, and the old one did it to all five.
 *
 * NOTHING IS WRITTEN TO STORAGE HERE. `writeSession` owns every key, so the
 * plaintext password, the email, the country and the surveillance-shaped
 * `cameraInfo` the old hook wrote (measured: zero readers, all four) stop being
 * written in ONE place rather than in four.
 */
const useLogin = () => {
  const { swalSuccess, swalFail, swalLoader, swalClose } = useSwal();
  const navigate = useNavigate();

  const [step, setStep] = useState('credentials');
  const [pending, setPending] = useState(false);
  const [providers, setProviders] = useState([]);
  // The sentence the SERVER wrote for the step we are on, rendered as text by
  // the form. There is no reason-code -> English map in the frontend.
  const [notice, setNotice] = useState('');

  // The credentials the cascade already proved, kept for the second half of a
  // link or a bootstrap. A ref, not state: they must never reach a render tree,
  // they must never reach storage, and nothing on screen depends on them.
  const proven = useRef(null);
  // A request can outlive the screen (the user navigates away mid-flight).
  const alive = useRef(true);
  // Two routes lead to the dashboard — the success dialog's OK button and the
  // timer behind it — and only the first to arrive may take it.
  const left = useRef(false);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /* The provider catalogue, so the form can SAY which other accounts work here
     without offering a choice between them. It carries no credential and needs
     no session, and a failure is silent on purpose: the line it feeds is an
     explanation, and a login screen reporting a broken explanation reports a
     problem nobody signing in can act on. */
  useEffect(() => {
    let ignore = false;
    (async () => {
      const res = await getProviders();
      if (ignore || !res.ok) return;
      setProviders(Array.isArray(res.data?.providers) ? res.data.providers : []);
    })();
    return () => {
      ignore = true;
    };
  }, []);

  const enter = useCallback(
    (data) => {
      // MSG_100 with no user is not a sign-in. `authProviders` already refuses a
      // body that is not an envelope, but an envelope whose `data` is empty
      // would still get here, and persisting that is a session that looks real
      // and has no workspace behind it.
      if (!data || !data.user) {
        swalFail(
          '<b>Error</b>',
          '<p>The sign-in was accepted but no workspace came back. Please try again.</p>',
        );
        return;
      }

      // The session is written BEFORE the dialog, not in its callback: the
      // dialog can be dismissed by an outside click, and a sign-in that
      // depended on somebody pressing OK would strand them on the login screen
      // with a valid session nobody stored.
      //
      // `writeSession` DOES NOT THROW, so a try/catch on its own proves nothing:
      // it catches a refusing `setItem` itself and it RETURNS FALSE — both when
      // storage is unusable and when the payload carries no usable user id. An
      // ignored boolean lands somebody on the dashboard with no session at all:
      // `getLoggedInOwnerId()` reads an empty string, every screen asks for an
      // owner that is not there, and the login screen has already told them they
      // are signed in. So the answer is read — and the session is read BACK,
      // because a swallowed storage failure inside the service still answers
      // true. The catch stays for the accessor itself throwing.
      let stored = false;
      try {
        stored = writeSession(data) !== false && Boolean(readSession().userId);
      } catch (error) {
        console.error('Could not store the session:', error);
      }
      if (!stored) {
        swalFail(
          '<b>Cannot stay signed in</b>',
          '<p>This browser would not store the session. Check its privacy settings and try again.</p>',
        );
        return;
      }
      if (Array.isArray(data?.providers)) setProviders(data.providers);

      const go = () => {
        if (left.current) return;
        left.current = true;
        swalClose();
        // The dialog is dismissed either way; the route change is only ours to
        // make while this screen is still the one on show.
        if (!alive.current) return;
        navigate('/Assistant');
      };

      swalSuccess('<b>Signed in</b>', summarise(data), go);
      // The summary is worth reading and must not be a gate. Whichever happens
      // first wins: OK, or this.
      setTimeout(go, 2600);
    },
    [navigate, swalClose, swalFail, swalSuccess],
  );

  /**
   * Every failure any of the three steps can answer with. One place, because the
   * link and bootstrap steps can be refused for exactly the same reasons as the
   * first one — and a second copy is how one of them ends up saying "wrong
   * password" to somebody who has been throttled.
   */
  const report = useCallback(
    (res) => {
      switch (res.message) {
        case MSG.REFUSED:
          swalFail(
            '<b>Cannot sign in</b>',
            `<p>${escapeHtml(res.detail || 'These credentials cannot be used to sign in here.')}</p>`,
          );
          break;
        case MSG.CLAIMED:
          swalFail(
            '<b>Already linked</b>',
            `<p>${escapeHtml(res.detail || 'That site is already linked to another workspace.')}</p>`,
          );
          break;
        case MSG.THROTTLED:
          swalFail('<b>Too many attempts</b>', '<p>Wait a few minutes and try again.</p>');
          break;
        case MSG.UNAUTHORISED:
          swalFail('<b>Warning</b>', '<p>Wrong email or password. Try again!</p>');
          break;
        default:
          swalFail('<b>Error</b>', '<p>Failed to log in. Please try again later.</p>');
          break;
      }
    },
    [swalFail],
  );

  /** Back to two fields, with nothing about the abandoned attempt kept. */
  const onCancelStep = useCallback(() => {
    proven.current = null;
    setNotice('');
    setStep('credentials');
  }, []);

  const onLogin = useCallback(
    async ({ email, password }) => {
      if (pending) return;
      setPending(true);
      swalLoader('<b>Signing in</b>', '<p>Checking your credentials...</p>');

      const res = await postLogin({ email, password });

      // Close the loader even if the screen is gone — an unmounted component
      // cannot dismiss a dialog that is still sitting over the next page.
      swalClose();
      if (!alive.current) return;
      setPending(false);

      if (res.ok) {
        // The workspace exists and the password was right, but the workspace was
        // MADE by a service sign-in and has no LabelFlow password of its own.
        // No session is persisted until it has one — otherwise the only way back
        // in tomorrow is the service credentials, and the day the service is
        // unreachable the workspace is unreachable with it.
        if (res.data?.mustSetPassword) {
          proven.current = { email, password, provider: res.data?.primaryProvider || '' };
          setNotice(
            `This workspace was created from your ${providerLabel(
              res.data?.providers,
              res.data?.primaryProvider,
            )} sign-in and has no LabelFlow password yet. Choose one now — you will need it to sign in directly and to connect services later.`,
          );
          setStep('bootstrap');
          return;
        }
        enter(res.data);
        return;
      }

      if (res.message === MSG.LINK_REQUIRED) {
        proven.current = { email, password, provider: '' };
        setNotice(
          res.detail ||
            'A LabelFlow account already uses this address. Enter its password to link the two.',
        );
        setStep('link');
        return; // No dialog: the form itself is the explanation and the remedy.
      }

      report(res);
    },
    [enter, pending, report, swalClose, swalLoader],
  );

  const onCompleteLink = useCallback(
    async ({ labelflowPassword }) => {
      const creds = proven.current;
      if (pending || !creds) return;
      setPending(true);
      swalLoader('<b>Linking</b>', '<p>Checking your LabelFlow password...</p>');

      const res = await postCompleteLink({
        email: creds.email,
        password: creds.password,
        labelflowPassword,
      });

      // Close the loader even if the screen is gone — an unmounted component
      // cannot dismiss a dialog that is still sitting over the next page.
      swalClose();
      if (!alive.current) return;
      setPending(false);

      if (res.ok) {
        proven.current = null;
        setNotice('');
        setStep('credentials');
        enter(res.data);
        return;
      }

      // A refusal here is about the SECOND password, and the step stays open so
      // it can be retyped. MSG_LINK_REQUIRED is never answered by this route.
      report(res);
    },
    [enter, pending, report, swalClose, swalLoader],
  );

  const onBootstrap = useCallback(
    async ({ newPassword }) => {
      const creds = proven.current;
      if (pending || !creds) return;

      // `/password/bootstrap` resolves the workspace BY THE PROVEN PROVIDER KEY,
      // so without one the call cannot succeed — and it would come back MSG_400,
      // which this hook reports as "this workspace already has a password".
      // That is the one wrong thing to say here: it sends somebody to sign in
      // with a password that does not exist. Ask for the credentials again
      // instead, which is the only thing that can produce the key.
      if (!creds.provider) {
        swalFail(
          '<b>Error</b>',
          '<p>That sign-in could not be identified any more. Please sign in again.</p>',
        );
        onCancelStep();
        return;
      }

      setPending(true);
      swalLoader('<b>Setting your password</b>', '<p>One moment...</p>');

      const res = await postBootstrapPassword({
        email: creds.email,
        password: creds.password,
        provider: creds.provider,
        newPassword,
      });

      // Close the loader even if the screen is gone — an unmounted component
      // cannot dismiss a dialog that is still sitting over the next page.
      swalClose();
      if (!alive.current) return;
      setPending(false);

      if (res.ok) {
        proven.current = null;
        setNotice('');
        setStep('credentials');
        enter(res.data);
        return;
      }

      // MSG_400 here has one meaning and it is not "bad request": the workspace
      // already has a password. Saying so is the difference between somebody
      // signing in and somebody inventing a third password.
      if (res.message === MSG.BAD_REQUEST) {
        swalFail(
          '<b>Already set</b>',
          '<p>This workspace already has a LabelFlow password. Sign in with that one.</p>',
        );
        onCancelStep();
        return;
      }

      report(res);
    },
    [enter, onCancelStep, pending, report, swalClose, swalFail, swalLoader],
  );

  const handlers = { onLogin, onCompleteLink, onBootstrap, onCancelStep };

  return { handlers, step, providers, pending, notice };
};

export default useLogin;
